import React, { useState } from "react";
import {
  Form,
  Flex,
  Card,
  Button,
  Col,
  Row,
  message,
  List,
  Select,
  Dropdown,
  Menu,
  Space,
} from "antd";
import { Location } from "../../../types/location";
import SalonSVG from "../../../assets/teamMember/SalonSVG";
import {
  PlusSquareOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import EditSVG from "../../../assets/teamMember/EditSVG";
import DeleteSVG from "../../../assets/common/DeleteSVG";
import { editTeamMember } from "../../../network/teamMember";
import { queryKeys } from "../../../services/react-query/queryKeys";
import queryClient from "../../../services/react-query/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAllLocations } from "../../../network/location";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

const LocationCard: React.FC<{
  item: Location & { name: { ar: string; en: string } };
  index: number;
  editing: boolean;
  handleRemoveLocation: (locationId: number, permission: string) => void;
}> = ({ item, index, editing, handleRemoveLocation }) => {
  const intl = useIntl();
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => {
      return {
        currentLang: state.user.currentLang,
      };
    }
  );
  return (
    <List.Item>
      <Card>
        <List.Item.Meta
          title={
            <div className="flex  pe-6 justify-between  ">
              <div className=" flex text-center bg-color-light rounded-md px-2">
                <span className="px-1 pt-3">
                  <SalonSVG />
                </span>
                <span className="py-2 px-1 	text-[18px] min-w-[170px]">
                  {item?.name[currentLang]}
                </span>
              </div>
              <div className="">
                {editing ? (
                  <>
                    <Button
                      type="text"
                      htmlType="button"
                      className="float-left block"
                      onClick={() =>
                        handleRemoveLocation(item.id, item.pivot.permission)
                      }
                    >
                      <DeleteSVG />
                    </Button>
                  </>
                ) : (
                  <Dropdown
                    className=""
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="delete"
                          onClick={() =>
                            handleRemoveLocation(item.id, item.pivot.permission)
                          }
                        >
                          {intl.formatMessage({
                            id: "delete",
                            defaultMessage: "مسح",
                          })}
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <Button className="border-none bg-transparent shadow-none outline-none">
                      <Space>
                        <MoreOutlined />
                      </Space>
                    </Button>
                  </Dropdown>
                )}
              </div>
            </div>
          }
        />
      </Card>
    </List.Item>
  );
};

const LocationInfo: React.FC<{ teamMemberData: any }> = ({
  teamMemberData,
}) => {
  const intl = useIntl();

  const [editing, setEditing] = useState(false);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [form] = Form.useForm();

  const { mutate: editMutation, isLoading } = useMutation<
    void,
    unknown,
    { location_permissions: { location_id: number; permission: string }[] }
  >((params) => editTeamMember(teamMemberData?.id, params), {
    onSuccess: (res) => {
      message.success("Edit success!");
      queryClient.invalidateQueries([queryKeys.listSpacficTeamMemer]);
      setEditing(false);
      setShowLocationSelector(false);
      setSelectedLocationIds([]);
    },
    onError: (error) => {
      message.error("حاول مرة اخرى");
    },
  });

  const onFinish = async (values: any) => {
    // Extract location IDs and permissions from teamMemberData
    const teamMemberLocationPermissions = (teamMemberData?.locations || []).map(
      (loc: Location & { name: { ar: string } }) => ({
        location_id: loc.id,
        permission: loc.pivot.permission,
      })
    );

    // Extract the rest of the data
    const oldLocationIds = teamMemberLocationPermissions.map(
      (loc: any) => loc.location_id
    );
    const newLocationIds = selectedLocationIds.filter(
      (id: any) => !oldLocationIds.includes(id)
    );

    if (newLocationIds.length === 0) {
      console.log("No new locations added.");
      return;
    }

    const locationPermissionsArray = selectedLocationIds.map((locationId) => ({
      location_id: locationId,
      permission: newLocationIds.includes(locationId)
        ? "no_access"
        : "default_permission", // Set the default value here
    }));

    // Combine the teamMemberLocationPermissions with the generated locationPermissionsArray
    values.location_permissions = [
      ...teamMemberLocationPermissions,
      ...locationPermissionsArray,
    ];
    console.log("values", values);

    editMutation(values);
  };

  const handleRemoveLocation = (locationId: number, permission: string) => {
    if (
      teamMemberData?.locations.some(
        (loc: { id: number }) => loc.id === locationId
      )
    ) {
      const updatedLocations = teamMemberData?.locations.filter(
        (loc: { id: number }) => loc.id !== locationId
      );
      const updatedLocationIds = updatedLocations.map(
        (loc: { id: number }) => loc.id
      );
      editMutation({
        location_permissions: updatedLocationIds.map((location_id: any) => ({
          location_id,
          permission,
        })),
      });
    } else {
      handleRemoveLocalLocation(locationId);
    }
  };

  const handleRemoveLocalLocation = (locationId: number) => {
    setSelectedLocationIds((prevIds) =>
      prevIds.filter((id) => id !== locationId)
    );
  };

  const handleAddLocationClick = () => {
    setShowLocationSelector(true);
  };

  const { data: listLocations } = useQuery([queryKeys.listLocations], () =>
    getAllLocations()
  );

  return (
    <div className="tabs bg-[#EEF0FF] p-4 ">
      <Flex justify="flex-end">
        <Button
          onClick={() => setEditing(!editing)}
          className="border-none bg-transparent shadow-none outline-none mt-2 md:mt-0"
        >
          <EditSVG />
        </Button>
      </Flex>

      <div className="text-base font-normal leading-7">
        {intl.formatMessage({
          id: "locationsMemberWorksIn",
          defaultMessage: "فروع المشروع التي يعمل بها العضو",
        })}
      </div>
      <List
        grid={{
          gutter: 14,
          xs: 1,
          sm: 2,
          md: 2,
          lg: 3,
          xl: 3,
          xxl: 4,
        }}
        className="mt-4"
        dataSource={[
          ...(teamMemberData?.locations || []),
          ...(listLocations?.data || []).filter((location: Location) =>
            selectedLocationIds.includes(location.id)
          ),
        ]}
        renderItem={(
          item: Location & { name: { ar: string; en: string } },
          index
        ) => (
          <>
            <LocationCard
              key={item.id}
              item={item}
              index={index}
              editing={editing}
              handleRemoveLocation={handleRemoveLocation}
            />
          </>
        )}
      />

      {editing && (
        <div>
          {showLocationSelector && (
            <Form
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                location_permissions: [""],
              }}
              form={form}
            >
              <Form.List name="location_permissions">
                {(fields, { add, remove }) => (
                  <>
                    <Form.Item className="p-2 rounded bg-color-light-gr w-full text-color-primary">
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Row
                          key={key}
                          gutter={{ xs: 8, sm: 16, md: 24, lg: 15 }}
                        >
                          <Col
                            className="gutter-row"
                            xl={11}
                            lg={12}
                            xs={24}
                            sm={12}
                            span={12}
                          >
                            <Form.Item
                              {...restField}
                              required={false}
                              name={[name, "location_id"]}
                            >
                              <Select
                                placeholder={intl.formatMessage({
                                  id: "selectLocation",
                                  defaultMessage: "إختر الفرع",
                                })}
                                className="w-full my-input"
                                options={listLocations?.data.map(
                                  (item: Location) => ({
                                    value: item?.id,
                                    label: item?.name,
                                  })
                                )}
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            className="gutter-row"
                            xl={1}
                            lg={12}
                            xs={24}
                            sm={12}
                            span={12}
                          >
                            <Form.Item>
                              <Button
                                className="border-none bg-transparent shadow-none outline-none"
                                onClick={() => {
                                  const oldLocationIds =
                                    teamMemberData?.locations.map(
                                      (loc: { id: number }) => loc.id
                                    );
                                  const selectedLocationId = form.getFieldValue(
                                    [
                                      "location_permissions",
                                      index,
                                      "location_id",
                                    ]
                                  );
                                  console.log(
                                    "selectedLocationId",
                                    selectedLocationId
                                  );
                                  const isLocationIdDuplicate =
                                    selectedLocationIds.includes(
                                      selectedLocationId
                                    ) ||
                                    oldLocationIds?.includes(
                                      selectedLocationId
                                    );

                                  if (!isLocationIdDuplicate) {
                                    setSelectedLocationIds((prevIds) => [
                                      ...prevIds,
                                      selectedLocationId,
                                    ]);
                                    remove(index);
                                    add();
                                  } else {
                                    message.error(
                                      "This location is already selected"
                                    );
                                  }
                                }}
                                block
                                icon={
                                  <PlusOutlined className="bg-[#00086D] text-white p-1 rounded-md p-0" />
                                }
                              ></Button>
                            </Form.Item>
                          </Col>
                        </Row>
                      ))}
                    </Form.Item>
                  </>
                )}
              </Form.List>
              <Flex gap="small" justify="flex-end">
                <Button
                  type="primary"
                  loading={isLoading}
                  className="text-white w-[164.5px]"
                  htmlType="submit"
                >
                  {intl.formatMessage({
                    id: "saveChanges",
                    defaultMessage: "حفظ التغييرات",
                  })}{" "}
                </Button>
                <Button
                  type="primary"
                  onClick={() => setEditing(!editing)}
                  className="bg-color-light text-black w-[164.5px]"
                  htmlType="button"
                >
                  {intl.formatMessage({
                    id: "cancel",
                    defaultMessage: "إلفاء",
                  })}
                </Button>
              </Flex>
            </Form>
          )}
          {!showLocationSelector && (
            <Flex gap="small" justify="flex-end">
              <Button
                type="primary"
                className="h-full"
                icon={<PlusSquareOutlined />}
                onClick={handleAddLocationClick}
              >
                {intl.formatMessage({
                  id: "addOtherLocation",
                  defaultMessage: "أضف فرع اخر",
                })}
              </Button>
            </Flex>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationInfo;
